import { lazyWithPreload } from "utils/lazyWithPreload"
import { prefetchLiveTracking } from "api/useSIPLiveTrains"
import type { Route, RoutePreloadDataContext } from "types/not-yet-sorted"
import { universalRoutes } from "./universal"

const LiveTracking = lazyWithPreload(() => import("pages/network-rail/LiveTracking"))
const SitesRequiringReview = lazyWithPreload(() => import("pages/network-rail/SitesRequiringReview"))
const TickSheets = lazyWithPreload(() => import("pages/network-rail/TickSheets"))
const ActionLogging = lazyWithPreload(() => import("pages/network-rail/ActionLogging"))
const WeatherOnly = lazyWithPreload(() => import("pages/network-rail/WeatherOnly"))

const PMTilesTreatmentAnalysis = lazyWithPreload(() => import("pages/network-rail/PMTilesTreatmentAnalysis"))
const AdhesionAnalysis = lazyWithPreload(() => import("pages/network-rail/AdhesionAnalysis"))
const NetworkView = lazyWithPreload(() => import("pages/network-rail/NetworkView"))
// Internal (super-admin) Customer Configuration — shared with roads; on NR it
// holds the control-desk visibility setup (ART-3177).
const Customers = lazyWithPreload(() => import("pages/roads/Customers"))

export const getSIPRoutes = ({
  actionLogging = false,
  weather = false,
  adhesion = false,
  srr = false,
  tickSheets = false,
  internal = false,
} = {}) => {
  const baseSIPRoutes = [
    {
      path: "/",
      component: LiveTracking,
      meta: { breadcrumb: [{ parent: "Dashboard", label: "Live Tracking" }] },
      // live trains + trails start while the page's chunk (deck.gl) downloads
      preloadData: ({ queryClient }: RoutePreloadDataContext) => {
        prefetchLiveTracking(queryClient).catch(() => undefined)
      },
    },
    {
      path: "/treatment-analysis",
      component: PMTilesTreatmentAnalysis,
      meta: {
        breadcrumb: [{ parent: "Dashboard", label: "Treatment Analysis" }],
      },
    },
  ]

  const actionLoggingRoute = {
    path: "/action-logging",
    component: ActionLogging,
    meta: {
      breadcrumb: [{ parent: "Dashboard", label: "Action Logging" }],
    },
  }

  const weatherRoute = {
    path: "/weather",
    component: WeatherOnly,
    meta: {
      breadcrumb: [{ parent: "Dashboard", label: "Weather" }],
    },
  }

  const adhesionRoute = {
    path: "/adhesion-analysis",
    component: AdhesionAnalysis,
    meta: {
      breadcrumb: [{ parent: "Dashboard", label: "Adhesion Analysis" }],
    },
  }

  const srrRoute = {
    path: "/srr",
    component: SitesRequiringReview,
    meta: {
      breadcrumb: [{ parent: "Dashboard", label: "Sites Requiring Review" }],
    },
  }

  const tickSheetsRoute = {
    path: "/nrol",
    component: TickSheets,
    meta: {
      breadcrumb: [{ parent: "Dashboard", label: "NROL Circuit Review" }],
    },
  }

  // Network-POV site view; ships alongside the NROL Circuit Review.
  const siteReviewRoute = {
    path: "/network-view",
    component: NetworkView,
    meta: { breadcrumb: [{ parent: "Dashboard", label: "NROL Site Review" }] },
  }

  let SIPRoutes: Route[] = baseSIPRoutes

  if (actionLogging) {
    SIPRoutes = [...SIPRoutes, actionLoggingRoute]
  } else {
    SIPRoutes = SIPRoutes.filter((r) => r.path !== actionLoggingRoute.path)
  }

  if (weather) {
    SIPRoutes = [...SIPRoutes, weatherRoute]
  } else {
    SIPRoutes = SIPRoutes.filter((r) => r.path !== weatherRoute.path)
  }

  if (adhesion) {
    SIPRoutes = [...SIPRoutes, adhesionRoute]
  } else {
    SIPRoutes = SIPRoutes.filter((r) => r.path !== adhesionRoute.path)
  }

  if (srr) {
    SIPRoutes = [...SIPRoutes, srrRoute]
  } else {
    SIPRoutes = SIPRoutes.filter((r) => r.path !== srrRoute.path)
  }

  if (tickSheets) {
    SIPRoutes = [...SIPRoutes, tickSheetsRoute, siteReviewRoute]
  } else {
    SIPRoutes = SIPRoutes.filter((r) => r.path !== tickSheetsRoute.path && r.path !== siteReviewRoute.path)
  }

  if (internal) {
    SIPRoutes = [
      ...SIPRoutes,
      {
        path: "/customers",
        component: Customers,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Customer Configuration" }] },
        internal: true,
      },
    ]
  }

  return [...SIPRoutes, ...universalRoutes]
}
